import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import {
  PhotoUploadQueueController,
  type PhotoUploadQueueApi,
  type PhotoUploadQueueView,
} from "./photo-upload-queue";

const QueueContext = createContext<PhotoUploadQueueApi | null>(null);

export function PhotoUploadQueueProvider({
  userId,
  children,
  apiBasePath,
  storageBasePath,
}: {
  userId: string | null | undefined;
  children: ReactNode;
  apiBasePath?: string;
  storageBasePath?: string;
}) {
  const controller = useMemo(
    () =>
      new PhotoUploadQueueController(
        userId ?? null,
        apiBasePath,
        storageBasePath,
      ),
    [apiBasePath, storageBasePath, userId],
  );

  useEffect(() => {
    void controller.start();
    return () => {
      controller.stop();
    };
  }, [controller, userId]);

  return <QueueContext.Provider value={controller}>{children}</QueueContext.Provider>;
}

export function usePhotoUploadQueue(): PhotoUploadQueueApi {
  const queue = useContext(QueueContext);
  if (!queue) {
    throw new Error("usePhotoUploadQueue must be used inside PhotoUploadQueueProvider");
  }
  return queue;
}

export function usePhotoUploadQueueJobs(
  draftItemId?: number,
): PhotoUploadQueueView[] {
  const queue = usePhotoUploadQueue();
  const jobs = useSyncExternalStore(queue.subscribe, queue.getJobs, queue.getJobs);
  return draftItemId === undefined
    ? jobs
    : jobs.filter((job) => job.draftItemId === draftItemId);
}